/**
 * Recargas de monto variable: «cobro 50% de lo que sea».
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EL PROBLEMA QUE ESTO RESUELVE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * El dueño, contando cómo trabaja el bodegón de su amigo:
 *
 *     «cobra 50% de cualquier recarga, es decir: de una recarga de 500 cobra
 *      750, de 1000 pues 1500 y así»
 *
 * Con precios fijos eso son un producto por cada monto —«Recarga 100»,
 * «Recarga 200», «Recarga 500»— y aun así nunca están todos: llega alguien
 * pidiendo 137 y no hay botón. Aquí hay UN servicio: el cajero escribe cuánto
 * y la cuenta la hace el sistema.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ ESTA CUENTA ES SOLO PARA ENSEÑARLA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Lo que se cobra de verdad lo calcula el servidor con `Decimal`, en
 * `_precio_de_recarga` (backend, `modules/pos/service.py`). Aquí se repite la
 * misma fórmula para que el cajero vea «cobras 750» mientras escribe, sin
 * esperar un viaje al servidor por cada tecla.
 *
 * Por eso la venta manda **el monto y no el precio**: si mandara el precio,
 * cualquiera con el punto de venta abierto podría cobrar una recarga de 500 a
 * 510 —o a 500— y la cuenta seguiría cuadrando, porque de la cuenta saldría lo
 * que él dijera. Con el monto, el recargo lo pone el catálogo.
 */

import type { ItemListRow } from "@/lib/types";

/** Lo que el catálogo le suma por encima al monto. */
export type Recargo = {
  /** En por ciento del monto: 50 son «la mitad más». */
  pct: number;
  /** Y en dinero, fijo. Se pueden usar los dos a la vez. */
  fijo: number;
};

/** Si al tocarlo hay que preguntar «¿de cuánto?». */
export function esDeMontoVariable(item: {
  kind?: string;
  monto_variable?: boolean;
}): boolean {
  return item.kind === "service" && Boolean(item.monto_variable);
}

export function recargoDe(item: {
  recargo_pct?: string | null;
  recargo_fijo?: string | null;
}): Recargo {
  return {
    pct: Number.parseFloat(item.recargo_pct ?? "0") || 0,
    fijo: Number.parseFloat(item.recargo_fijo ?? "0") || 0,
  };
}

/**
 * Lo que se le cobra al cliente por una recarga de `monto`.
 *
 *     cobro = monto × (1 + pct/100) + fijo
 *
 * ⚠️ Se redondea a dos decimales igual que el servidor. Sin redondear aquí, la
 * pantalla diría 750.0000000001 en el peor momento: con el cliente delante.
 */
export function loQueSeCobra(monto: number, recargo: Recargo): number {
  if (!Number.isFinite(monto) || monto <= 0) return 0;
  const bruto = monto * (1 + recargo.pct / 100) + recargo.fijo;
  return Math.round(bruto * 100) / 100;
}

/** Lo que se queda el negocio: la diferencia entre lo cobrado y lo recargado. */
export function loQueSeGana(monto: number, recargo: Recargo): number {
  return Math.round((loQueSeCobra(monto, recargo) - monto) * 100) / 100;
}

/**
 * Cómo se dice el recargo en una línea: «+50%», «+2,00», «+50% y +2,00».
 *
 * Se usa en la ficha del catálogo y en la franja del punto de venta, para que
 * las dos pantallas describan el mismo servicio con las mismas palabras.
 */
export function comoSeCobra(recargo: Recargo, simbolo: string): string {
  const partes: string[] = [];
  if (recargo.pct > 0) partes.push(`+${formatearNumero(recargo.pct)}%`);
  if (recargo.fijo > 0) partes.push(`+${simbolo}${formatearNumero(recargo.fijo)}`);
  if (partes.length === 0) return "sin recargo";
  return partes.join(" y ");
}

/**
 * Un número sin ceros de adorno: 50 y no «50,00»; 2,5 y no «2,50».
 *
 * El recargo no es dinero que se cobre —es una regla— y escribir «+50,00%» lo
 * hace parecer una cifra medida al céntimo cuando es simplemente «la mitad».
 */
function formatearNumero(n: number): string {
  return String(Math.round(n * 100) / 100).replace(".", ",");
}

/**
 * Los montos que se piden todo el día, para ponerlos de botón.
 *
 * ⚠️ NO SON FIJOS: SALEN DE LO QUE EL NEGOCIO YA VENDIÓ.
 *
 * Una lista escrita a mano —100, 200, 500, 1000— sirve para el bodegón de
 * Caracas y no sirve para ninguno más: en un negocio que recarga en bolívares
 * los montos son otros, y en uno que vende Robux también. Estos salen del
 * historial de ese servicio en ese negocio, así que a la semana los cuatro
 * botones son los cuatro montos que de verdad pide su gente.
 *
 * Se ordenan por cuántas veces se pidió, y luego de menor a mayor para que no
 * salten de sitio cada vez que alguien recarga.
 */
export function montosFrecuentes(historial: number[], cuantos = 4): number[] {
  const veces = new Map<number, number>();
  for (const m of historial) {
    if (!Number.isFinite(m) || m <= 0) continue;
    veces.set(m, (veces.get(m) ?? 0) + 1);
  }
  return [...veces.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, cuantos)
    .map(([monto]) => monto)
    .sort((a, b) => a - b);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 *  LOS MONTOS DE ATAJO SE APRENDEN, NO SE ESCRIBEN
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Se guardan EN EL DISPOSITIVO, no en el servidor. Tres razones:
 *
 *   1. Funciona sin internet, que es donde este punto de venta tiene que
 *      funcionar mejor que ninguno.
 *   2. No cuesta una petición por cada toque de un producto.
 *   3. Si se pierde no pasa nada: son cuatro botones de comodidad. Volverlos a
 *      aprender es cuestión de una mañana de ventas.
 *
 * Se guardan los últimos 40 montos por servicio —de ahí salen los cuatro más
 * repetidos— y como mucho 60 servicios. Sin tope, esto crecería para siempre
 * en el navegador de una tablet que nadie limpia nunca.
 */

const LLAVE = "softfi-montos-de-recarga";
const CUANTOS_GUARDA = 40;
const CUANTOS_SERVICIOS = 60;

type MemoriaDeMontos = Record<string, number[]>;

function leerMemoria(): MemoriaDeMontos {
  try {
    const crudo = window.localStorage.getItem(LLAVE);
    if (!crudo) return {};
    const datos = JSON.parse(crudo) as unknown;
    if (!datos || typeof datos !== "object" || Array.isArray(datos)) return {};
    return datos as MemoriaDeMontos;
  } catch {
    // Modo incógnito, almacenamiento lleno, o un JSON de una versión vieja.
    // Ninguna de las tres es motivo para que el punto de venta deje de abrir.
    return {};
  }
}

/** Los montos que más se han pedido de este servicio, en este dispositivo. */
export function atajosDe(itemId: string, cuantos = 4): number[] {
  return montosFrecuentes(leerMemoria()[itemId] ?? [], cuantos);
}

/** Apunta un monto recién vendido, para que mañana sea un botón. */
export function recordarMonto(itemId: string, monto: number): void {
  if (!Number.isFinite(monto) || monto <= 0) return;
  try {
    const memoria = leerMemoria();
    const suyos = [monto, ...(memoria[itemId] ?? [])].slice(0, CUANTOS_GUARDA);
    memoria[itemId] = suyos;

    // El más viejo se cae cuando hay demasiados servicios distintos.
    const llaves = Object.keys(memoria);
    if (llaves.length > CUANTOS_SERVICIOS) {
      for (const vieja of llaves.slice(0, llaves.length - CUANTOS_SERVICIOS)) {
        delete memoria[vieja];
      }
    }

    window.localStorage.setItem(LLAVE, JSON.stringify(memoria));
  } catch {
    // Que no se pueda recordar un atajo no puede impedir cobrar una recarga.
  }
}

/**
 * Lo que hace falta saber de un ítem para preguntarle el monto.
 *
 * Se acepta la fila ligera del listado porque es lo que tiene la grilla del
 * punto de venta en la mano al recibir el toque.
 */
export type ItemDeRecarga = Pick<ItemListRow, "id" | "name"> & {
  monto_variable?: boolean;
  recargo_pct?: string | null;
  recargo_fijo?: string | null;
};
